
// Libraries
import { Component, namespace, Prop, Ref, Vue } from "nuxt-property-decorator";

// Components
import PriceComparisonH1 from "~/components/organisms/display/PriceComparisonH1.vue";
import PelletsPriceComparison from "~/components/organisms/form/PelletsPriceComparison.vue";
import PriceOffersSection from "~/components/shared/PriceOffersSection.vue";

// Helpers
import sortingDataParemeters from "~/defaults/heizoelpreise/sortingDataParemeters";
import sortOptions from "~/defaults/heizoelpreise/sortOptions";
import pelletsSortOptions from "~/defaults/pellets/pelletsSortOptions";
import { PriceValues } from "~/operations/messages";
import { SortOption } from "~/types/HeatingOilCalculator";

const CalculatorModule = namespace("calculator");

@Component({
  components: {
    PelletsPriceComparison,
    PriceOffersSection,
    PriceComparisonH1,
  },
})
export default class PelletsPriceDisplay extends Vue {
  @Prop({ required: true }) cityData!: string;
  @Prop({ required: true }) zipcode!: string;

  @Ref("priceComparison")
  priceComparison: PelletsPriceComparison;

  @CalculatorModule.State("dealerOffersData")
  dealerOffersData: any;

  @CalculatorModule.Action("fetchDealerOffers")
  _fetchDealerOffers;

  @CalculatorModule.Action("fetchAndSortDealerOffers")
  _fetchAndSortDealerOffers;

  selectPelletsOption = this.currentPelletstype;
  showPriceOffers = true;

  alternativeOverlayMessageType: "none" | "guaranteedPayment" = "none";

  sortingDataParemeters = sortingDataParemeters;
  pelletsSortOptions = pelletsSortOptions;
  sortOptions: SortOption = sortOptions;
  PriceValues = PriceValues;

  async fetch() {
    await this.fetchDealerOffers();
  }

  async fetchDealerOffers() {
    const requestData = this.getRequestData();
    const dealerOfferRequest = { body: requestData, isPelletsSite: true };
    await this._fetchDealerOffers(dealerOfferRequest);
  }

  resortData(sortOptions: {
    sortByParameter: string;
    direction: "asc" | "desc";
  }) {
    this.sortingDataParemeters.sortByParameter = sortOptions.sortByParameter;
    this.sortingDataParemeters.direction = sortOptions.direction;
    this.$store.commit("calculator/sortDealerOffers", {
      ...sortOptions,
      isPelletsSite: true,
    });
  }

  getRequestData() {
    return {
      zipcode: this.zipcode,
      amount: Number(this.$route.query.amount ?? 6000),
      unloading_points: Number(this.$route.query.unloading_points ?? 1),
      payment_type: this.$route.query.payment_type ?? "all",
      prod: this.$route.query.prod ?? "loose",
      quality_steps: Array.isArray(this.$route.query.quality_steps)
        ? this.$route.query.quality_steps
        : this.$route.query.quality_steps
        ? this.$route.query.quality_steps.split(",").map((item) => item.trim())
        : ["isoNorm", "enPlus", "dinPlus"],
      hose: this.$route.query.hose ?? "thirtyMetre",
      short_vehicle: this.$route.query.short_vehicle ?? "withTrailer",
      deliveryTimes: this.$route.query.deliveryTimes ?? "normal",
      dust_protection: false,
    };
  }

  updatePelletsValue(value: string): void {
    this.selectPelletsOption = value;
    this.showPriceOffers = false;
  }

  hidePriceOffersContent(status: boolean) {
    this.updateAlternativeOverlayMessageType();
    this.showPriceOffers = status;
  }

  updateAlternativeOverlayMessageType() {
    if (this.priceComparison.requestData.payment_type === "guaranteedPayment") {
      this.alternativeOverlayMessageType = "guaranteedPayment";
    } else {
      this.alternativeOverlayMessageType = "none";
    }
  }

  onSelectGuaranteedPayment() {
    this.priceComparison.requestData.payment_type = "guaranteedPayment";
    this.priceComparison.$el.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  get alternativeOverlayMessage() {
    switch (this.alternativeOverlayMessageType) {
      case "guaranteedPayment":
        return 'Bitte bestätigen Sie den "Rechnen" Button, um Ihre Ergebnisse für Ratenkauf zu erhalten.';
      default:
        return undefined;
    }
  }

  async fetchAndSortDealerOffers(data) {
    // Copy the data object so multiplying the amount does not mutate the input value
    let apiData = { ...data };

    if (
      this.selectPelletsOption === "pallet" ||
      this.selectPelletsOption === "bigbag"
    ) {
      apiData.amount = apiData.amount * 1000;
    }

    apiData = {
      ...apiData,
      prod: this.selectPelletsOption,
      quality_steps: apiData.prod.split(",").map((item) => item.trim()),
    };

    await this._fetchAndSortDealerOffers({
      isPelletsSite: true,
      dealerParameters: apiData,
      sortOptions: this.sortingDataParemeters,
    });
    this.showPriceOffers = true;
  }

  get showDealerOffers(): boolean {
    return (
      this.dealerOffersData.errorMessage === "" &&
      this.dealerOffersData.dealerOffers.length > 0
    );
  }

  get showPaletColumn(): boolean {
    return this.selectPelletsOption === "pallet" && this.showPriceOffers;
  }

  get currentPelletstype() {
    return this.$route.query.prod ?? "loose";
  }
}
